import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import type {
    TimeRange} from "@@/shared/date-time/time-range";
import {
    isTimeRange,
    timeRangeFactory,
} from "@@/shared/date-time/time-range";
import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInWeeks,
    differenceInYears,
    isPast,
    type Duration,
} from "date-fns";
import { isBookable } from "../acquire/is-bookable";
import { assertNever } from "../assert-never";
import { asDate } from "../date-time/as-date";
import type { Order } from "./order";

const getEstimatedOrderFulfillmentTime = (
    order: Order,
): IsoAndUnixTimestamp | TimeRange | undefined => {
    if (!order.delivery.estimatedDeliveryTime) return undefined;

    // Bookable workaround
    if (isBookable(order)) {
        // Find bookable order item with a time set
        const bookableOrderItem = order.orderItems.find(
            orderItem => orderItem.time && isBookable(orderItem),
        );
        if (bookableOrderItem?.time) return bookableOrderItem.time;
    }

    switch (order.delivery.deliveryMethodType) {
        case "PICK_UP":
            return order.delivery.estimatedDeliveryTime;
        case "HOME_DELIVERY":
            if (order.delivery.shippingSlot) {
                return timeRangeFactory(
                    order.delivery.estimatedDeliveryTime,
                    order.delivery.shippingSlot.start,
                );
            }
            return order.delivery.estimatedDeliveryTime;
        case "DIGITAL":
            return order.delivery.estimatedDeliveryTime;
        default:
            assertNever(order?.delivery);
            throw new Error(
                "Unhandled delivery method type; " +
                    order.delivery.deliveryMethodType,
            );
    }
};

const howLateIsOrder = (
    order: Order,
): {
    fulfillmentTime: IsoAndUnixTimestamp | TimeRange | undefined;
    durationSince: Required<Duration>;
} => {
    const estimatedFulfillmentTime = getEstimatedOrderFulfillmentTime(order);
    if (!estimatedFulfillmentTime) {
        // Since there is no estimated delivery date
        // the order can't be late
        const result: Required<Duration> = {
            minutes: 0,
            hours: 0,
            days: 0,
            months: 0,
            seconds: 0,
            weeks: 0,
            years: 0,
        };
        return {
            fulfillmentTime: estimatedFulfillmentTime,
            durationSince: result,
        };
    }

    const fulfillmentTime = isTimeRange(estimatedFulfillmentTime)
        ? asDate(estimatedFulfillmentTime.end)
        : asDate(estimatedFulfillmentTime);

    const now = new Date();
    const result: Required<Duration> = {
        minutes: -differenceInMinutes(fulfillmentTime, now),
        hours: -differenceInHours(fulfillmentTime, now),
        days: -differenceInDays(fulfillmentTime, now),
        months: -differenceInMonths(fulfillmentTime, now),
        seconds: -differenceInSeconds(fulfillmentTime, now),
        weeks: -differenceInWeeks(fulfillmentTime, now),
        years: -differenceInYears(fulfillmentTime, now),
    };
    return {
        fulfillmentTime: estimatedFulfillmentTime,
        durationSince: result,
    };
};

const isOrderLate = (order: Order): boolean => {
    const estimatedFulfillmentTime = getEstimatedOrderFulfillmentTime(order);
    if (!estimatedFulfillmentTime) {
        // Since there is no estimated delivery date
        // the order can't be late
        return false;
    }

    const isLate = isTimeRange(estimatedFulfillmentTime)
        ? isPast(asDate(estimatedFulfillmentTime.end))
        : isPast(asDate(estimatedFulfillmentTime));

    return isLate;
};

export { getEstimatedOrderFulfillmentTime, howLateIsOrder, isOrderLate };
